//@ts-check
// @ts-check
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ja';

dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);

const epochMillisecondsByInstance = new WeakMap();
const dayStartHourByInstance = new WeakMap();

/**
 * @typedef {Object} CTIDayBusinessInput
 * @property {number} date YYYYMMDD
 * @property {number} [time] Minutes from midnight. Values over 1440 are allowed.
 * @property {number} [second]
 * @property {number} [millisecond]
 */

/**
 * @typedef {Object} CTIDayCalendarInput
 * @property {number} year
 * @property {number} month 1-12
 * @property {number} day
 * @property {number} [hour]
 * @property {number} [minute]
 * @property {number} [second]
 * @property {number} [millisecond]
 */

/** @typedef {CTIDay | import('dayjs').Dayjs | Date | number | string | CTIDayBusinessInput | CTIDayCalendarInput | null | undefined} CTIDayInput */
/** @typedef {'day' | 'week' | 'month' | 'year'} CTIDayPeriodUnit */
/** @typedef {[year: number, month: number, day: number]} CTIDayDateArray */
/** @typedef {[hour: number, minute: number, second: number, millisecond: number]} CTIDayTimeArray */
/** @typedef {[year: number, month: number, day: number, hour: number, minute: number, second: number, millisecond: number]} CTIDayDateTimeArray */
/** @typedef {{ year: number, month: number, day: number }} CTIDayDateParts */
/** @typedef {{ hour?: number, minute?: number, second?: number, millisecond?: number }} CTIDayTimeUpdate */
/** @typedef {{ year: number, month: number, day: number, hour: number, minute: number, second: number, millisecond: number }} CTIDayParts */
/** @typedef {{ now: () => number }} CTIDayClock */
/** @typedef {{ basis?: 'business' | 'calendar', weekStartsOn?: number }} CTIDayPeriodOptions */
/** @typedef {{ basis?: 'instant' | 'business' | 'calendar', weekStartsOn?: number }} CTIDayComparisonOptions */
/** @typedef {{ inclusiveStart?: boolean, inclusiveEnd?: boolean }} CTIDayBetweenOptions */
/** @typedef {{ preserve?: 'instant' | 'businessDateTime' }} CTIDayStartHourOptions */
/** @typedef {{ dateBasis?: 'business' | 'calendar', hourStyle?: 'extended' | 'nextDay' | 'clock', locale?: string }} CTIDayFormatOptions */

/** @param {string} unit @returns {import('dayjs').OpUnitType | 'quarter'} */
const normalizeUnit = (unit) => {
  /** @type {Record<string, import('dayjs').OpUnitType | 'quarter'>} */
  const aliases = { y: 'year', M: 'month', w: 'week', d: 'day', D: 'date', h: 'hour', m: 'minute', s: 'second', ms: 'millisecond', Q: 'quarter' };
  return aliases[unit] || /** @type {import('dayjs').OpUnitType | 'quarter'} */ (String(unit).toLowerCase().replace(/s$/, ''));
};

/** @param {string} unit @returns {boolean} */
const isPeriodUnit = unit => ['day', 'date', 'week', 'month', 'quarter', 'year'].includes(normalizeUnit(unit));

/** @param {number} dayStartHour @returns {boolean} */
const isDayStartHour = dayStartHour => Number.isInteger(dayStartHour) && 0 <= dayStartHour && dayStartHour <= 23;

/** @param {CTIDay} value @returns {import('dayjs').Dayjs} */
const businessDate = (value) => {
  const date = value.toDate();
  date.setHours(date.getHours() - value.getDayStartHour());
  return dayjs(date);
};

/**
 * @param {number} dateInt
 * @returns {CTIDayDateParts | null}
 */
const parseDateInt = (dateInt) => {
  if (!Number.isInteger(dateInt)) {
    return null;
  }

  const text = String(dateInt);
  if (!/^\d{8}$/.test(text)) {
    return null;
  }

  const year = Number(text.slice(0, 4));
  const month = Number(text.slice(4, 6));
  const day = Number(text.slice(6, 8));
  const date = new Date(year, month - 1, day, 12);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return { year, month, day };
};

/**
 * @param {Date} date
 * @returns {number}
 */
const toDateInt = (date) => (
  date.getFullYear() * 10000 +
  (date.getMonth() + 1) * 100 +
  date.getDate()
);

/**
 * @param {CTIDayCalendarInput} parts
 * @returns {number}
 */
const createLocalEpoch = ({
  year,
  month,
  day,
  hour = 0,
  minute = 0,
  second = 0,
  millisecond = 0,
}) => {
  if (![year, month, day, hour, minute, second, millisecond].every(Number.isFinite)) return NaN;
  const dateParts = parseDateInt(year * 10000 + month * 100 + day);
  if (!dateParts) return NaN;
  const date = new Date(
    dateParts.year,
    dateParts.month - 1,
    dateParts.day,
    0,
    0,
    0,
    0,
  );
  date.setHours(hour, minute, second, millisecond);

  const epochMilliseconds = date.getTime();
  if (!Number.isFinite(epochMilliseconds)) {
    return NaN;
  }
  return epochMilliseconds;
};

export class CTIDay {
  /**
   * @param {number} epochMilliseconds
   * @param {number} dayStartHour
   */
  constructor(epochMilliseconds, dayStartHour) {
    const valid = isDayStartHour(dayStartHour) && typeof epochMilliseconds === 'number' && Number.isFinite(epochMilliseconds);
    epochMillisecondsByInstance.set(this, valid ? new Date(epochMilliseconds).getTime() : NaN);
    dayStartHourByInstance.set(this, dayStartHour);
    Object.freeze(this);
  }

  /**
   * @param {number} epochMilliseconds
   * @param {number} dayStartHour
   * @returns {CTIDay}
   */
  static fromEpochMilliseconds(epochMilliseconds, dayStartHour) {
    return new CTIDay(epochMilliseconds, dayStartHour);
  }

  /**
   * @param {Date} date
   * @param {number} dayStartHour
   * @returns {CTIDay}
   */
  static fromDate(date, dayStartHour) {
    return new CTIDay(date instanceof Date ? date.getTime() : NaN, dayStartHour);
  }

  /**
   * @param {number} dateInt
   * @param {number} dayStartHour
   * @returns {CTIDay}
   */
  static fromDateInt(dateInt, dayStartHour) {
    const parts = parseDateInt(dateInt);
    if (!parts) return new CTIDay(NaN, dayStartHour);
    return CTIDay.fromCalendarParts({
      ...parts,
      hour: dayStartHour,
    }, dayStartHour);
  }

  /**
   * @param {number} dateInt
   * @param {number} hour60Minute
   * @param {number} dayStartHour
   * @returns {CTIDay}
   */
  static fromDateAndHm(dateInt, hour60Minute, dayStartHour) {
    const parts = parseDateInt(dateInt);
    if (!parts || !Number.isInteger(hour60Minute)) return new CTIDay(NaN, dayStartHour);
    // A clock time before the business boundary belongs to the following calendar day.
    const minute = 0 <= hour60Minute && hour60Minute < dayStartHour * 60
      ? hour60Minute + 1440
      : hour60Minute;
    return new CTIDay(createLocalEpoch({ ...parts, minute }), dayStartHour);
  }

  /**
   * @param {CTIDayCalendarInput} parts
   * @param {number} dayStartHour
   * @returns {CTIDay}
   */
  static fromCalendarParts(parts, dayStartHour) {
    return new CTIDay(parts && typeof parts === 'object' ? createLocalEpoch(parts) : NaN, dayStartHour);
  }

  /**
   * @param {CTIDayCalendarInput} parts
   * @param {number} dayStartHour
   * @returns {CTIDay}
   */
  static fromBusinessParts(parts, dayStartHour) {
    if (!parts) return new CTIDay(NaN, dayStartHour);
    const hour = parts.hour ?? dayStartHour;
    return new CTIDay(createLocalEpoch({
      ...parts,
      hour: 0 <= hour && hour < dayStartHour ? hour + 24 : hour,
    }), dayStartHour);
  }

  /**
   * @param {number} dayStartHour
   * @param {CTIDayClock} [clock]
   * @returns {CTIDay}
   */
  static now(dayStartHour, clock = Date) {
    return new CTIDay(typeof clock?.now === 'function' ? clock.now() : NaN, dayStartHour);
  }

  /**
   * @param {number} dayStartHour
   * @param {CTIDayClock} [clock]
   * @returns {CTIDay}
   */
  static today(dayStartHour, clock = Date) {
    return CTIDay.fromDateInt(
      CTIDay.now(dayStartHour, clock).toDateInt(),
      dayStartHour,
    );
  }

  /**
   * @param {string} text
   * @param {string | undefined} pattern
   * @param {number} dayStartHour
   * @returns {CTIDay}
   */
  static parse(text, pattern, dayStartHour) {
    if (typeof text !== 'string' || (pattern !== undefined && typeof pattern !== 'string')) {
      return new CTIDay(NaN, dayStartHour);
    }
    const datePatterns = { 4: 'YYYY', 6: 'YYYYMM', 8: 'YYYYMMDD' };
    const datePattern = /^\d{4}(?:[-/]\d{1,2}){1,2}$/.test(text)
      ? text.split(/([-/])/).map((part, index) => index === 0 ? 'YYYY' : index === 2 ? 'M'.repeat(part.length) : index === 4 ? 'D'.repeat(part.length) : part).join('')
      : /^\d+$/.test(text) ? datePatterns[text.length] : undefined;
    const format = pattern || datePattern;
    // Avoid dayjs's permissive rollover for invalid ISO/local calendar dates.
    const datePrefix = text.match(/^(\d{4})[-/](\d{2})[-/](\d{2})(?=[ T]|$)/);
    if (datePrefix && !parseDateInt(Number(datePrefix[1] + datePrefix[2] + datePrefix[3]))) {
      return new CTIDay(NaN, dayStartHour);
    }
    const parsed = format ? dayjs(text, format, true) : dayjs(text);
    if (!parsed.isValid()) return new CTIDay(NaN, dayStartHour);
    const dateOnly = format && !/[HhmsSaAZZ]/.test(format.replace(/\[[^\]]*\]/g, ''));
    return dateOnly
      ? CTIDay.fromDateInt(Number(parsed.format('YYYYMMDD')), dayStartHour)
      : new CTIDay(parsed.valueOf(), dayStartHour);
  }

  /**
   * @param {CTIDay} left
   * @param {CTIDayInput} right
   * @param {import('dayjs').OpUnitType} [unit]
   * @returns {number}
   */
  static compare(left, right, unit = 'millisecond') {
    if (!(left instanceof CTIDay)) return NaN;
    const other = ctiday(right, left.getDayStartHour());
    if (!left.isValid() || !other.isValid()) return NaN;
    if (left.isSame(other, unit)) return 0;
    return left.isBefore(other, unit) ? -1 : 1;
  }

  /**
   * @param {CTIDay} value
   * @param {import('dayjs').ManipulateType} unit
   * @param {number} amount
   * @returns {CTIDay}
   */
  static add(value, unit, amount) {
    if (!(value instanceof CTIDay)) return new CTIDay(NaN, NaN);
    if (!Number.isFinite(amount) || !value.isValid()) return new CTIDay(NaN, value.getDayStartHour());
    const normalized = normalizeUnit(unit);
    if (isPeriodUnit(normalized)) {
      const date = dayjs(String(value.toDateInt())).add(
        normalized === 'quarter' ? amount * 3 : amount,
        normalized === 'date' ? 'day' : normalized === 'quarter' ? 'month' : /** @type {import('dayjs').ManipulateType} */ (normalized),
      );
      return value.withBusinessDate({ year: date.year(), month: date.month() + 1, day: date.date() });
    }
    return new CTIDay(dayjs(value.valueOf()).add(amount, unit).valueOf(), value.getDayStartHour());
  }

  /**
   * @param {CTIDay} value
   * @param {import('dayjs').ManipulateType} unit
   * @param {number} amount
   * @returns {CTIDay}
   */
  static subtract(value, unit, amount) {
    return CTIDay.add(value, unit, -amount);
  }

  /**
   * @param {CTIDay} left
   * @param {CTIDayInput} right
   * @param {import('dayjs').QUnitType} [unit]
   * @param {boolean} [float]
   * @returns {number}
   */
  static diff(left, right, unit = 'millisecond', float = false) {
    if (!(left instanceof CTIDay)) return NaN;
    const other = ctiday(right, left.getDayStartHour());
    if (!left.isValid() || !other.isValid()) return NaN;
    const normalized = normalizeUnit(unit);
    const diffUnit = /** @type {import('dayjs').QUnitType} */ (normalized === 'date' ? 'day' : normalized);
    return isPeriodUnit(normalized)
      ? businessDate(left).diff(businessDate(other), diffUnit, float)
      : dayjs(left.valueOf()).diff(dayjs(other.valueOf()), diffUnit, float);
  }

  /**
   * @param {CTIDay} value
   * @param {import('dayjs').OpUnitType} unit
   * @param {CTIDayPeriodOptions} [options]
   * @returns {CTIDay}
   */
  static startOf(value, unit, { basis = 'business', weekStartsOn = 1 } = {}) {
    if (!(value instanceof CTIDay)) return new CTIDay(NaN, NaN);
    if (!value.isValid() || !['business', 'calendar'].includes(basis)) return new CTIDay(NaN, value.getDayStartHour());
    const normalized = normalizeUnit(unit);
    if (basis === 'calendar' || !isPeriodUnit(normalized)) {
      return new CTIDay(dayjs(value.valueOf()).startOf(unit).valueOf(), value.getDayStartHour());
    }
    let date = businessDate(value).startOf('day');
    if (normalized === 'week') {
      if (!Number.isInteger(weekStartsOn) || weekStartsOn < 0 || 6 < weekStartsOn) return new CTIDay(NaN, value.getDayStartHour());
      date = date.subtract((date.day() - weekStartsOn + 7) % 7, 'day');
    } else if (normalized === 'quarter') {
      date = date.month(Math.floor(date.month() / 3) * 3).startOf('month');
    } else if (normalized !== 'day' && normalized !== 'date') {
      date = date.startOf(normalized);
    }
    return CTIDay.fromDateInt(Number(date.format('YYYYMMDD')), value.getDayStartHour());
  }

  /**
   * @param {CTIDay} value
   * @param {import('dayjs').OpUnitType} unit
   * @param {CTIDayPeriodOptions} [options]
   * @returns {{ start: CTIDay, endExclusive: CTIDay }}
   */
  static rangeOf(value, unit, options = {}) {
    const start = CTIDay.startOf(value, unit, options);
    const normalized = normalizeUnit(unit);
    const nextUnit = /** @type {import('dayjs').ManipulateType} */ (normalized === 'date' ? 'day' : normalized);
    const endExclusive = options.basis === 'calendar'
      ? new CTIDay(dayjs(start.valueOf()).add(1, nextUnit).valueOf(), start.getDayStartHour())
      : CTIDay.add(start, nextUnit, 1);
    return { start, endExclusive };
  }

  /** @returns {boolean} */
  isValid() {
    return Number.isFinite(this.valueOf());
  }

  /** @returns {number} */
  unix() {
    return Math.floor(this.valueOf() / 1000);
  }

  /** @returns {CTIDay} */
  clone() {
    return new CTIDay(this.valueOf(), this.getDayStartHour());
  }

  /** @param {number} amount @param {import('dayjs').ManipulateType} unit @returns {CTIDay} */
  add(amount, unit) {
    return CTIDay.add(this, unit, amount);
  }

  /** @param {number} amount @param {import('dayjs').ManipulateType} unit @returns {CTIDay} */
  subtract(amount, unit) {
    return CTIDay.subtract(this, unit, amount);
  }

  /** @param {CTIDayInput} other @param {import('dayjs').QUnitType} [unit] @param {boolean} [float] @returns {number} */
  diff(other, unit = 'millisecond', float = false) {
    return CTIDay.diff(this, ctiday(other, this.getDayStartHour()), unit, float);
  }

  /** @param {import('dayjs').OpUnitType} unit @param {CTIDayPeriodOptions} [options] @returns {CTIDay} */
  startOf(unit, options) {
    return CTIDay.startOf(this, unit, options);
  }

  /** @param {import('dayjs').OpUnitType} unit @param {CTIDayPeriodOptions} [options] @returns {{ start: CTIDay, endExclusive: CTIDay }} */
  rangeOf(unit, options) {
    return CTIDay.rangeOf(this, unit, options);
  }

  /** @param {import('dayjs').OpUnitType} unit @param {CTIDayPeriodOptions} [options] @returns {CTIDay} */
  endOf(unit, options) {
    return this.rangeOf(unit, options).endExclusive.subtract(1, 'millisecond');
  }

  /** @returns {number} */
  daysInMonth() {
    return businessDate(this).daysInMonth();
  }

  /** @param {import('dayjs').UnitType} unit @param {number} value @returns {CTIDay} */
  set(unit, value) {
    const normalized = normalizeUnit(unit);
    switch (normalized) {
      case 'year': return this.year(value);
      case 'month': return this.month(value);
      case 'date': return this.date(value);
      case 'day': return this.day(value);
      case 'hour': return this.hour(value);
      case 'minute': return this.minute(value);
      case 'second': return this.second(value);
      case 'millisecond': return this.millisecond(value);
      default: return new CTIDay(NaN, this.getDayStartHour());
    }
  }

  /** @param {import('dayjs').UnitType} unit @returns {number} */
  get(unit) {
    switch (normalizeUnit(unit)) {
      case 'year': return this.year();
      case 'month': return this.month();
      case 'date': return this.date();
      case 'day': return this.day();
      case 'hour': return this.hour();
      case 'minute': return this.minute();
      case 'second': return this.second();
      case 'millisecond': return this.millisecond();
      default: return NaN;
    }
  }

  /** @param {boolean} [withoutSuffix] @returns {string} */
  fromNow(withoutSuffix = false) {
    return this.isValid() ? dayjs(this.valueOf()).locale('ja').fromNow(withoutSuffix) : 'Invalid Date';
  }

  /** @returns {number} */
  getDayStartHour() {
    return dayStartHourByInstance.get(this);
  }

  /** @returns {number} */
  toEpochMilliseconds() {
    return epochMillisecondsByInstance.get(this);
  }

  /** @returns {number} */
  toLong() {
    return this.toEpochMilliseconds();
  }

  /** @returns {number} */
  valueOf() {
    return this.toEpochMilliseconds();
  }

  /** @returns {Date} */
  toDate() {
    return new Date(this.toEpochMilliseconds());
  }

  /** @returns {number} */
  toDateInt() {
    const date = new Date(this.toEpochMilliseconds());
    date.setHours(date.getHours() - this.getDayStartHour());
    return toDateInt(date);
  }

  /** @returns {number} */
  toCalendarDateInt() {
    return toDateInt(new Date(this.toEpochMilliseconds()));
  }

  /** @returns {number} */
  toHour60Minute() {
    const date = new Date(this.toEpochMilliseconds());
    const hour = date.getHours() < this.getDayStartHour()
      ? date.getHours() + 24
      : date.getHours();
    return hour * 60 + date.getMinutes();
  }

  /** @overload @returns {number} */
  /** @overload @param {number} value @returns {CTIDay} */
  /** @param {number} [value] @returns {number | CTIDay} */
  year(value) {
    if (value === undefined) return Math.floor(this.toDateInt() / 10000);
    if (!Number.isFinite(value) || !this.isValid()) return new CTIDay(NaN, this.getDayStartHour());
    const date = dayjs(String(this.toDateInt())).year(Math.trunc(value));
    return this.withBusinessDate({ year: date.year(), month: date.month() + 1, day: date.date() });
  }

  /** @overload @returns {number} */
  /** @overload @param {number} value @returns {CTIDay} */
  /** @param {number} [value] @returns {number | CTIDay} */
  month(value) {
    if (value === undefined) return Math.floor(this.toDateInt() / 100) % 100;
    if (!Number.isFinite(value) || !this.isValid()) return new CTIDay(NaN, this.getDayStartHour());
    const date = dayjs(String(this.toDateInt())).month(Math.trunc(value) - 1);
    return this.withBusinessDate({ year: date.year(), month: date.month() + 1, day: date.date() });
  }

  /** @overload @returns {number} */
  /** @overload @param {number} value @returns {CTIDay} */
  /** @param {number} [value] @returns {number | CTIDay} */
  day(value) {
    const weekday = businessDate(this).day();
    if (value === undefined) return weekday;
    return Number.isFinite(value) ? this.add(Math.trunc(value) - weekday, 'day') : new CTIDay(NaN, this.getDayStartHour());
  }

  /** @overload @returns {number} */
  /** @overload @param {number} value @returns {CTIDay} */
  /** @param {number} [value] @returns {number | CTIDay} */
  date(value) {
    if (value === undefined) return this.toDateInt() % 100;
    if (!Number.isFinite(value) || !this.isValid()) return new CTIDay(NaN, this.getDayStartHour());
    return this.add(Math.trunc(value) - this.date(), 'day');
  }

  /** @overload @returns {number} */
  /** @overload @param {number} value @returns {CTIDay} */
  /** @param {number} [value] @returns {number | CTIDay} */
  hour(value) {
    if (value === undefined) {
      return Math.floor(this.toHour60Minute() / 60);
    }
    if (!Number.isFinite(value)) return new CTIDay(NaN, this.getDayStartHour());
    value = Math.trunc(value);
    return this.withBusinessTime({ hour: value });
  }

  /** @overload @returns {number} */
  /** @overload @param {number} value @returns {CTIDay} */
  /** @param {number} [value] @returns {number | CTIDay} */
  minute(value) {
    if (value === undefined) {
      return new Date(this.toEpochMilliseconds()).getMinutes();
    }
    if (!Number.isFinite(value)) return new CTIDay(NaN, this.getDayStartHour());
    value = Math.trunc(value);
    return this.withBusinessTime({ minute: value });
  }

  /** @overload @returns {number} */
  /** @overload @param {number} value @returns {CTIDay} */
  /** @param {number} [value] @returns {number | CTIDay} */
  second(value) {
    if (value === undefined) {
      return new Date(this.toEpochMilliseconds()).getSeconds();
    }
    if (!Number.isFinite(value)) return new CTIDay(NaN, this.getDayStartHour());
    value = Math.trunc(value);
    return this.withBusinessTime({ second: value });
  }

  /** @overload @returns {number} */
  /** @overload @param {number} value @returns {CTIDay} */
  /** @param {number} [value] @returns {number | CTIDay} */
  millisecond(value) {
    if (value === undefined) {
      return new Date(this.toEpochMilliseconds()).getMilliseconds();
    }
    if (!Number.isFinite(value)) return new CTIDay(NaN, this.getDayStartHour());
    value = Math.trunc(value);
    return this.withBusinessTime({ millisecond: value });
  }

  /** @overload @returns {number} */
  /** @overload @param {number} value @returns {CTIDay} */
  /** @param {number} [value] @returns {number | CTIDay} */
  calendarHour(value) {
    if (value === undefined) {
      return new Date(this.toEpochMilliseconds()).getHours();
    }
    if (!Number.isFinite(value)) return new CTIDay(NaN, this.getDayStartHour());
    value = Math.trunc(value);
    return this.withCalendarTime({ hour: value });
  }

  /** @returns {CTIDayParts} */
  getBusinessParts() {
    return {
      year: this.year(),
      month: this.month(),
      day: this.date(),
      hour: this.hour(),
      minute: this.minute(),
      second: this.second(),
      millisecond: this.millisecond(),
    };
  }

  /** @returns {CTIDayParts} */
  getCalendarParts() {
    const date = new Date(this.toEpochMilliseconds());
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
      hour: date.getHours(),
      minute: date.getMinutes(),
      second: date.getSeconds(),
      millisecond: date.getMilliseconds(),
    };
  }

  /** @returns {CTIDayDateArray} */
  getDateArray() {
    return [this.year(), this.month(), this.date()];
  }

  /** @returns {CTIDayTimeArray} */
  getTimeArray() {
    return [this.hour(), this.minute(), this.second(), this.millisecond()];
  }

  /** @returns {CTIDayDateTimeArray} */
  getDateTimeArray() {
    return [...this.getDateArray(), ...this.getTimeArray()];
  }

  /** @param {number} value @returns {CTIDay} */
  withHour60Minute(value) {
    const base = CTIDay.fromDateAndHm(this.toDateInt(), value, this.getDayStartHour());
    return base.add(this.second() * 1000 + this.millisecond(), 'millisecond');
  }

  /**
   * @param {CTIDayDateParts} parts
   * @returns {CTIDay}
   */
  withBusinessDate({ year, month, day }) {
    return CTIDay.fromBusinessParts({
      ...this.getBusinessParts(),
      year,
      month,
      day,
    }, this.getDayStartHour());
  }

  /**
   * @param {CTIDayTimeUpdate} parts
   * @returns {CTIDay}
   */
  withBusinessTime(parts) {
    return CTIDay.fromBusinessParts({
      ...this.getBusinessParts(),
      ...parts,
    }, this.getDayStartHour());
  }

  /**
   * @param {CTIDayCalendarInput} parts
   * @returns {CTIDay}
   */
  withBusinessDateTime(parts) {
    return CTIDay.fromBusinessParts(parts, this.getDayStartHour());
  }

  /**
   * @param {CTIDayDateParts} parts
   * @returns {CTIDay}
   */
  withCalendarDate({ year, month, day }) {
    return CTIDay.fromCalendarParts({
      ...this.getCalendarParts(),
      year,
      month,
      day,
    }, this.getDayStartHour());
  }

  /**
   * @param {CTIDayTimeUpdate} parts
   * @returns {CTIDay}
   */
  withCalendarTime(parts) {
    return CTIDay.fromCalendarParts({
      ...this.getCalendarParts(),
      ...parts,
    }, this.getDayStartHour());
  }

  /**
   * @param {CTIDayCalendarInput} parts
   * @returns {CTIDay}
   */
  withCalendarDateTime(parts) {
    return CTIDay.fromCalendarParts(parts, this.getDayStartHour());
  }

  /**
   * @param {number} dayStartHour
   * @param {CTIDayStartHourOptions} [options]
   * @returns {CTIDay}
   */
  withDayStartHour(dayStartHour, { preserve = 'instant' } = {}) {
    if (preserve === 'instant') {
      return new CTIDay(this.toEpochMilliseconds(), dayStartHour);
    }
    if (preserve === 'businessDateTime') {
      return CTIDay.fromBusinessParts(this.getBusinessParts(), dayStartHour);
    }
    return new CTIDay(NaN, dayStartHour);
  }

  /**
   * @param {CTIDayInput} other
   * @returns {boolean}
   */
  equals(other) {
    return this.isSame(other);
  }

  /**
   * @param {CTIDayInput} other
   * @param {import('dayjs').OpUnitType} [unit]
   * @param {CTIDayPeriodOptions} [options]
   * @returns {boolean}
   */
  isBefore(other, unit = 'millisecond', options = {}) {
    const target = ctiday(other, this.getDayStartHour());
    return this.isValid() && target.isValid() && this.endOf(unit, options).valueOf() < target.valueOf();
  }

  /**
   * @param {CTIDayInput} other
   * @param {import('dayjs').OpUnitType} [unit]
   * @param {CTIDayPeriodOptions} [options]
   * @returns {boolean}
   */
  isAfter(other, unit = 'millisecond', options = {}) {
    const target = ctiday(other, this.getDayStartHour());
    return this.isValid() && target.isValid() && target.valueOf() < this.startOf(unit, options).valueOf();
  }

  /**
   * @param {CTIDayInput} other
   * @param {import('dayjs').OpUnitType} [unit]
   * @param {CTIDayComparisonOptions} [options]
   * @returns {boolean}
   */
  isSame(other, unit = 'millisecond', { basis = 'business', weekStartsOn = 1 } = {}) {
    const target = ctiday(other, this.getDayStartHour());
    if (!this.isValid() || !target.isValid()) return false;
    const options = { basis: basis === 'instant' ? 'calendar' : basis, weekStartsOn };
    const { start, endExclusive } = this.rangeOf(unit, options);
    return start.valueOf() <= target.valueOf() && target.valueOf() < endExclusive.valueOf();
  }

  /** @param {CTIDayInput} other @param {import('dayjs').OpUnitType} [unit] @returns {boolean} */
  isSameOrBefore(other, unit = 'millisecond') {
    return this.isSame(other, unit) || this.isBefore(other, unit);
  }

  /** @param {CTIDayInput} other @param {import('dayjs').OpUnitType} [unit] @returns {boolean} */
  isSameOrAfter(other, unit = 'millisecond') {
    return this.isSame(other, unit) || this.isAfter(other, unit);
  }

  /**
   * @param {CTIDayInput} start
   * @param {CTIDayInput} end
   * @param {CTIDayBetweenOptions} [options]
   * @returns {boolean}
   */
  isBetween(start, end, { inclusiveStart = true, inclusiveEnd = false } = {}) {
    const left = ctiday(start, this.getDayStartHour());
    const right = ctiday(end, this.getDayStartHour());
    if (!this.isValid() || !left.isValid() || !right.isValid()) return false;
    const value = this.valueOf();
    const afterStart = inclusiveStart ? left.valueOf() <= value : left.valueOf() < value;
    const beforeEnd = inclusiveEnd ? value <= right.valueOf() : value < right.valueOf();
    return afterStart && beforeEnd;
  }

  /**
   * @param {string} pattern
   * @param {CTIDayFormatOptions} [options]
   * @returns {string}
   */
  format(pattern, {
    dateBasis = 'business',
    hourStyle = 'extended',
    locale = 'ja',
  } = {}) {
    if (dateBasis !== 'business' && dateBasis !== 'calendar') {
      return 'Invalid Date';
    }
    if (!['extended', 'nextDay', 'clock'].includes(hourStyle)) {
      return 'Invalid Date';
    }

    if (!this.isValid() || typeof pattern !== 'string') return 'Invalid Date';
    const calendarDate = dayjs(this.valueOf()).locale(locale);
    const displayDate = dateBasis === 'business' ? businessDate(this).locale(locale) : calendarDate;
    const hour = hourStyle === 'extended' ? this.hour() : calendarDate.hour();
    const prefix = hourStyle === 'nextDay' && this.hour() >= 24 ? '翌' : '';
    // Delegate complete tokens to dayjs; only 24+ hour notation needs custom output.
    return pattern.replace(
      /\[[^\]]*\]|YYYY|YY|MMMM|MMM|MM|M|DD|D|dddd|ddd|dd|d|HH|H|hh|h|mm|m|ss|s|SSS|ZZ|Z|A|a/g,
      token => {
        if (token.startsWith('[')) return token.slice(1, -1);
        if (token === 'HH') return `${prefix}${String(hour).padStart(2, '0')}`;
        if (token === 'H') return `${prefix}${hour}`;
        return (/^(?:Y|M|D|d)/.test(token) ? displayDate : calendarDate).format(token);
      },
    );
  }

  /** @returns {string} */
  toYmdString() {
    return this.format('YYYY/MM/DD');
  }

  /**
   * @param {CTIDayFormatOptions} [options]
   * @returns {string}
   */
  toHmString(options) {
    return this.format('HH:mm', options);
  }

  /**
   * @param {CTIDayFormatOptions} [options]
   * @returns {string}
   */
  toYmdHmString(options) {
    return this.format('YYYY/MM/DD(dd) HH:mm', options);
  }
}

/**
 * Create a CTIDay from one of the supported input types.
 * @param {CTIDayInput} value
 * @param {number} dayStartHour
 * @returns {CTIDay}
 */
export const ctiday = (value, dayStartHour) => {
  if (value === undefined) return CTIDay.now(dayStartHour);
  if (value === null) return new CTIDay(NaN, dayStartHour);
  if (value instanceof CTIDay || dayjs.isDayjs(value)) {
    return CTIDay.fromEpochMilliseconds(value.valueOf(), dayStartHour);
  }
  if (value instanceof Date) return CTIDay.fromDate(value, dayStartHour);
  if (typeof value === 'number') {
    return /^\d{8}$/.test(String(value))
      ? CTIDay.fromDateInt(value, dayStartHour)
      : CTIDay.fromEpochMilliseconds(value, dayStartHour);
  }
  if (typeof value === 'string') return CTIDay.parse(value, undefined, dayStartHour);
  if (typeof value !== 'object') return new CTIDay(NaN, dayStartHour);
  if ('date' in value) {
    const result = CTIDay.fromDateAndHm(value.date, value.time ?? dayStartHour * 60, dayStartHour);
    return result.add((value.second ?? 0) * 1000 + (value.millisecond ?? 0), 'millisecond');
  }
  return CTIDay.fromBusinessParts(value, dayStartHour);
};

export default ctiday;
